import {
  AllowedMaterialSpec,
  MaterialComponentCategory,
  SiteMaterialDetail,
  SiteMaterialList,
  useApiTradeV1SiteMaterialsRetrieve,
} from 'api';
import { MaterialSupportedComponentList } from 'features';
import {
  AllowedMaterialSpecAutocomplete,
  BooleanSelectField,
  BuyerMaterialSpecificationsAutocomplete,
  MaterialGradeAutocomplete,
  MaterialOriginAutocomplete,
  SiteMaterialWithProductAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  useFormContext,
  useWatch,
} from 'form';
import { useTranslation } from 'i18n';
import { useContext, useEffect } from 'react';
import { Box, Button, Divider, Grid, Typography } from 'ui';

import { MaterialDirtLevel } from '../../../material';
import { SalesOrderFormContext } from '../../contexts/SalesOrderFormContext';
import { BaseSalesOrderFormValues } from '../../types';
import { isMaterialProductSyncedWithMaterialProps } from './isMaterialProductSyncedWithMaterialProps';
import { updateMaterialProperties } from './updateMaterialProperties';

export const BuyerMaterialDetailsSection = () => {
  const { t } = useTranslation('orders');
  const { setValue, watch, getValues } = useFormContext<BaseSalesOrderFormValues>();
  const { purchaser_id: purchaserId } = useWatch<BaseSalesOrderFormValues>();

  const { variant } = useContext(SalesOrderFormContext);
  const siteMaterialId = watch('site_material_id') || '';
  const sellerId = watch('seller_id');
  const materialId = watch('material_id');
  const materialColourId = watch('material_colour_id');
  const { data: siteMaterialDetail } = useApiTradeV1SiteMaterialsRetrieve(siteMaterialId?.toString());
  const values = getValues();
  const onSyncMaterial = (siteMaterialDetail: SiteMaterialDetail | SiteMaterialList | undefined) => {
    if (!siteMaterialDetail) {
      return;
    }
    updateMaterialProperties(siteMaterialDetail, setValue);
  };

  const onAllowedMaterialSpecChange = (value: AllowedMaterialSpec) => {
    setValue('material_id', value.material_id);
    setValue('material_colour_id', value.colour_id ?? undefined);
  };

  useEffect(() => {
    if (!sellerId && siteMaterialDetail?.site?.trade_company?.id) {
      setValue('seller_id', siteMaterialDetail?.site?.trade_company?.id);
    }
  }, [sellerId, siteMaterialDetail, setValue]);

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">
        {t('buyerMaterialDetailsSection.title')}
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{
          marginBottom: 2,
          marginTop: 0,
        }}
      >
        <Grid
          size={{
            md: 8,
            xs: 12,
          }}
        >
          <BuyerMaterialSpecificationsAutocomplete
            name="buyer_material_spec_id"
            tradeCompanyId={purchaserId}
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.buyerMaterialSpecification')}
          />
        </Grid>
        <Grid
          size={{
            md: 8,
            xs: 12,
          }}
        >
          <TradeCompanyAutocomplete
            name="seller_id"
            label={t('buyerMaterialDetailsSection.labels.seller')}
            onlySellers={true}
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.seller')}
          />
        </Grid>
        <Grid
          size={{
            md: 8,
            xs: 12,
          }}
        >
          <SiteMaterialWithProductAutocomplete
            label={t('buyerMaterialDetailsSection.labels.siteMaterial')}
            name="site_material_id"
            tradeCompanyId={sellerId}
            onSelectOption={(siteMaterial) => onSyncMaterial(siteMaterial)}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <Button
            disabled={isMaterialProductSyncedWithMaterialProps(values, siteMaterialDetail)}
            onClick={() => onSyncMaterial(siteMaterialDetail)}
          >
            {t('buyerMaterialDetailsSection.actions.syncMaterialProperties')}
          </Button>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <AllowedMaterialSpecAutocomplete
            name="material_spec_id"
            label={t('buyerMaterialDetailsSection.labels.material')}
            required
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.material')}
            onSelectOption={onAllowedMaterialSpecChange}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <MaterialGradeAutocomplete
            label={t('buyerMaterialDetailsSection.labels.grade')}
            name="grade_id"
            required
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.grade')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <MaterialOriginAutocomplete
            label={t('buyerMaterialDetailsSection.labels.origin')}
            name="origin_id"
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.origin')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <MaterialDirtLevel
            label={t('buyerMaterialDetailsSection.labels.dirtLevel')}
            name="dirt_level"
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.dirtLevel')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <BooleanSelectField
            label={t('buyerMaterialDetailsSection.labels.foodGrade')}
            name="is_food_grade"
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.foodGrade')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <BooleanSelectField
            label={t('buyerMaterialDetailsSection.labels.containsCapsAndLabels')}
            name="has_caps_and_labels"
            withNotAvailable
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.containsCapsAndLabels')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TextField
            label={t('buyerMaterialDetailsSection.labels.materialEwcCodeOverride')}
            name="material_ewc_code_override"
            type="text"
            fullWidth
            required={false}
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.materialEwcCodeOverride')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TextField
            label={t('buyerMaterialDetailsSection.labels.materialCustomsNameOverride')}
            name="material_customs_name_override"
            type="text"
            fullWidth
            required={false}
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.materialCustomsNameOverride')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TextField
            label={t('buyerMaterialDetailsSection.labels.baselCodeOverride')}
            name="basel_code_override"
            slotProps={{
              htmlInput: {
                maxLength: 8,
              },
            }}
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.baselCodeOverride')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          {variant === 'update' ? (
            <TextField
              label={t('buyerMaterialDetailsSection.labels.materialHsCodeOverride')}
              name="material_hs_code_override"
              type="text"
              fullWidth
              required={false}
              infoTooltip={t('buyerMaterialDetailsSection.tooltips.materialHsCodeOverride')}
            />
          ) : null}
        </Grid>
      </Grid>
      <MaterialSupportedComponentList
        name="seller_material_component_values"
        materialId={materialId}
        materialColourId={materialColourId}
        title={t('buyerMaterialDetailsSection.supportedComponents.sellerTitle')}
        buttonText={t('buyerMaterialDetailsSection.supportedComponents.addSeller')}
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        disabled
      />
      <MaterialSupportedComponentList
        name="buyer_material_component_values"
        materialId={materialId}
        materialColourId={materialColourId}
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        title={t('buyerMaterialDetailsSection.supportedComponents.buyerTitle')}
        buttonText={t('buyerMaterialDetailsSection.supportedComponents.addBuyer')}
      />
      <Divider />
    </Box>
  );
};
